import { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type LicenseType = 'standard' | 'exclusive';

export interface CartListingInput {
  listingId: string;
  assetId: string;
  title: string;
  category: string;
  thumbnailUrl?: string | null;
  creatorName?: string | null;
  offerStandard: boolean;
  offerExclusive: boolean;
  priceCents: number;
  priceStandard?: number | null;
  priceExclusive?: number | null;
}

export interface CartItem extends CartListingInput {
  licenseType: LicenseType;
}

interface CartContextValue {
  items: CartItem[];
  itemCount: number;
  totalCents: number;
  addItem: (input: CartListingInput, licenseType?: LicenseType) => void;
  updateLicense: (listingId: string, licenseType: LicenseType) => void;
  removeItem: (listingId: string) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

const STORAGE_KEY = 'forgeblocks-cart';

const resolveLicense = (input: CartListingInput, requested?: LicenseType): LicenseType => {
  if (requested) return requested;
  if (input.offerStandard) return 'standard';
  return 'exclusive';
};

const priceForLicense = (item: CartListingInput, license: LicenseType) => {
  if (license === 'exclusive') {
    return item.priceExclusive ?? item.priceCents;
  }
  return item.priceStandard ?? item.priceCents;
};

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed as CartItem[];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore persistence failures */
    }
  }, [items]);

  const addItem = (input: CartListingInput, licenseType?: LicenseType) => {
    const requested = licenseType;
    const license = resolveLicense(
      input,
      requested === 'exclusive' && input.offerExclusive
        ? 'exclusive'
        : requested === 'standard' && input.offerStandard
          ? 'standard'
          : undefined
    );
    const next: CartItem = {
      ...input,
      licenseType: license,
    };

    setItems((prev) => {
      const existingIndex = prev.findIndex((item) => item.listingId === input.listingId);
      if (existingIndex >= 0) {
        const copy = [...prev];
        copy[existingIndex] = next;
        return copy;
      }
      return [...prev, next];
    });
  };

  const updateLicense = (listingId: string, licenseType: LicenseType) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.listingId !== listingId) return item;
        const allowed =
          (licenseType === 'exclusive' && item.offerExclusive) ||
          (licenseType === 'standard' && item.offerStandard);
        if (!allowed) return item;
        return { ...item, licenseType };
      })
    );
  };

  const removeItem = (listingId: string) => {
    setItems((prev) => prev.filter((item) => item.listingId !== listingId));
  };

  const clearCart = () => setItems([]);

  const totalCents = useMemo(
    () => items.reduce((sum, item) => sum + priceForLicense(item, item.licenseType), 0),
    [items]
  );

  const value = useMemo(
    () => ({
      items,
      itemCount: items.length,
      totalCents,
      addItem,
      updateLicense,
      removeItem,
      clearCart,
    }),
    [items, totalCents]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
};

export const getItemPrice = (item: CartItem) => priceForLicense(item, item.licenseType);
